import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../api.js';
import { useSession } from '../auth-client.js';
import CompetitionPicker from '../components/CompetitionPicker.js';
import CoverImageUploader from '../components/CoverImageUploader.js';
import LockForeverToggle from '../components/LockForeverToggle.js';
import TeamPicker, { type TeamScope } from '../components/TeamPicker.js';

/**
 * Mounted at `/groups/:id/bolaos/new/football` — adding a football bolão to
 * an already-existing circle (PLAN.md Phase 10.18; circle creation itself is
 * a separate, earlier step — see `CreateCircleScreen`). `api.createPool`
 * has no `joinAs` param (joining is always a separate step for an existing
 * circle's bolão), so a filled-in `joinAs` becomes a follow-up
 * `api.joinPool` call once creation succeeds.
 *
 * Once a competition is picked, `TeamPicker` (PLAN.md 10.25) offers narrowing
 * it to specific teams — "just the Barcelona and Madrid games" — rather than
 * the whole season. Reset whenever `competitionId` changes, since a team
 * selected for one competition means nothing (and may not even exist) once
 * the dropdown moves to another.
 */
export default function CreateGroupScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const locale = i18n.resolvedLanguage ?? 'en';

  const [name, setName] = useState('');
  const [competitionId, setCompetitionId] = useState('');
  const [joinAs, setJoinAs] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [teamScope, setTeamScope] = useState<TeamScope>('all');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  // Checked by default: the mechanic already worked unconditionally for
  // every bolão before this toggle existed (see `bolaos.allowLockForever`'s
  // doc comment), so a new bolão keeps that behaviour unless the organizer
  // deliberately opts out.
  const [allowLockForever, setAllowLockForever] = useState(true);
  const { data: session } = useSession();

  // Switching to a different competition invalidates whatever was picked for
  // the last one — a team id from La Liga means nothing once the dropdown
  // moves to the World Cup, and silently keeping it would submit a
  // filter full of ids that don't belong to the new competition at all.
  useEffect(() => {
    setTeamScope('all');
    setSelectedTeamIds([]);
  }, [competitionId]);

  // Defaults "play as" to the organizer's own account name — most people
  // organizing a bolão are also playing in it, so this saves retyping their
  // own name every time. `!joinAs` keeps it from clobbering anything they've
  // already typed once the session finishes loading.
  useEffect(() => {
    if (session?.user?.name && !joinAs) setJoinAs(session.user.name);
  }, [session?.user?.name, joinAs]);

  const { data: competitions } = useQuery({
    queryKey: ['competitions'],
    queryFn: api.listCompetitions,
  });

  const createPool = useMutation({
    mutationFn: async () => {
      const pool = await api.createPool(id, {
        name: name.trim(),
        competitionId,
        allowLockForever,
        ...(imageUrl ? { imageUrl } : {}),
        ...(teamScope === 'teams' ? { teamIds: selectedTeamIds } : {}),
      });
      if (joinAs.trim()) await api.joinPool(id, pool.id, joinAs.trim());
      return pool;
    },
    onSuccess: async (pool) => {
      await queryClient.invalidateQueries({ queryKey: ['pools', id] });
      void navigate(`/groups/${id}/bolaos/${pool.id}`);
    },
  });

  // A competition whose ruleset no longer resolves cannot be scored, so offering
  // it would let someone build a bolão that silently fails later. Scoped to
  // football since that's the only sport this route is reachable for
  // (PLAN.md Phase 10.3's sport picker is what links here).
  const selectable = competitions?.filter((c) => c.scoreable && c.sportId === 'football') ?? [];
  // "Specific teams" with nothing actually picked yet is not a submittable
  // state — the API rejects an empty list rather than silently treating it
  // as "all teams", and the button should say so before the round trip does.
  const teamSelectionValid = teamScope === 'all' || selectedTeamIds.length > 0;
  const canSubmit =
    name.trim().length > 0 &&
    competitionId.length > 0 &&
    teamSelectionValid &&
    !createPool.isPending;

  return (
    <main className="screen">
      <Link to={`/groups/${id}/bolaos/new`} className="muted">
        {t('common.back')}
      </Link>
      <h1>{t('createCompetition.title')}</h1>

      <form
        className="panel stack"
        style={{ padding: 'var(--space-4)' }}
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) createPool.mutate();
        }}
      >
        <div>
          <label className="label" htmlFor="name">
            {t('createCompetition.nameLabel')}
          </label>
          <input
            id="name"
            className="input"
            value={name}
            maxLength={80}
            placeholder={t('createCompetition.namePlaceholder')}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div>
          <label className="label">{t('createCompetition.competitionLabel')}</label>
          <CompetitionPicker
            competitions={selectable}
            value={competitionId}
            onChange={setCompetitionId}
          />
        </div>

        {competitionId ? (
          <TeamPicker
            competitionId={competitionId}
            locale={locale}
            scope={teamScope}
            onScopeChange={setTeamScope}
            selectedTeamIds={selectedTeamIds}
            onSelectedTeamIdsChange={setSelectedTeamIds}
          />
        ) : null}

        <div>
          <label className="label" htmlFor="joinAs">
            {t('createCompetition.joinLabel')}
          </label>
          <input
            id="joinAs"
            className="input"
            value={joinAs}
            maxLength={60}
            placeholder={t('createCompetition.joinPlaceholder')}
            onChange={(event) => setJoinAs(event.target.value)}
          />
          <p className="muted" style={{ fontSize: '0.8rem', marginTop: 'var(--space-1)' }}>
            {t('createCompetition.joinHint')}
          </p>
        </div>

        <LockForeverToggle checked={allowLockForever} onChange={setAllowLockForever} />

        <div>
          <span className="label">{t('createCompetition.imageLabel')}</span>
          <CoverImageUploader
            imageUrl={imageUrl}
            onChange={setImageUrl}
            disabled={createPool.isPending}
          />
        </div>

        {createPool.isError ? <p className="error">{t('common.somethingWentWrong')}</p> : null}

        <button type="submit" className="button button-primary" disabled={!canSubmit}>
          {createPool.isPending ? t('createCompetition.creating') : t('createCompetition.submit')}
        </button>
      </form>
    </main>
  );
}
